import { useEffect, useState } from "react";
import { listProjectTestRuns, type QaTestRunSummary } from "../api/qaOrchestrator";
import { relativeTime } from "./admin/memberModel";
import type { QaTab } from "../lib/route";
import "./admin/qa-orchestrator.css";
import "./admin/qa-catalog.css";

type Props = {
  projects: { id: string; name: string }[];
  onOpenProject: (id: string, tab?: QaTab) => void;
  onClose: () => void;
};

type Latest = { configured: boolean; repo: string; run: QaTestRunSummary | null } | "error";

// /qa: every project the user can see, with its latest test run. Picking one
// opens its QA tests page; "Tests" opens its catalog of tests.
export default function QaOverview({ projects, onOpenProject, onClose }: Props) {
  const [latest, setLatest] = useState<Record<string, Latest>>({});
  useEffect(() => {
    let live = true;
    for (const p of projects) {
      listProjectTestRuns(p.id)
        .then((r) => live && setLatest((m) => ({ ...m, [p.id]: { configured: r.configured, repo: r.repo, run: r.testRuns[0] ?? null } })))
        .catch(() => live && setLatest((m) => ({ ...m, [p.id]: "error" })));
    }
    return () => { live = false; };
  }, [projects]);

  const unconfigured = Object.values(latest).some((l) => l !== "error" && !l.configured);
  return (
    <div className="qa-page">
      <header className="qa-page-header">
        <button type="button" className="qa-page-back" onClick={onClose} aria-label="Back to chat">←</button>
        <h1>QA tests</h1>
      </header>
      {unconfigured && <p className="qo-hint">QA testing is not set up yet. An admin connects it under Manage → QA.</p>}
      {projects.length === 0 && <p className="qo-hint">You do not have access to any projects yet.</p>}
      {projects.map((p) => {
        const l = latest[p.id];
        return (
          <div key={p.id} className="qo-run qc-overview-row">
            <button type="button" className="qo-run-row" onClick={() => onOpenProject(p.id)}>
              <strong className="qo-grow">{p.name}</strong>
              {l === undefined && <span className="qo-muted">Loading…</span>}
              {l === "error" && <span className="qo-error-text">Could not load</span>}
              {l && l !== "error" && (l.run
                ? <><span className={`qo-pill qo-pill-${l.run.status}`}>{l.run.status}</span><code>{l.run.ref}</code><span className="qo-muted">{relativeTime(l.run.createdAt)}</span></>
                : <span className="qo-muted">{l.repo ? `No runs yet · ${l.repo}` : "No GitHub repo"}</span>)}
            </button>
            <button type="button" className="qc-overview-tests" onClick={() => onOpenProject(p.id, "tests")} aria-label={`Tests of ${p.name}`}>Tests</button>
          </div>
        );
      })}
    </div>
  );
}
